import React, { useState, useEffect, useMemo } from "react";
import {
  MapPin,
  Calendar,
  Star,
  ArrowRight,
  LogOut,
  Award,
  DollarSign,
  Headphones,
  Shield,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import translations from "../contexts/translations";
import HeroSection from "../components/HeroSection";
import ServiceCard from "../components/ServiceCard";
import { motion } from "framer-motion";
import {
  servicePlaceholders,
  destinationPlaceholders,
  profilePlaceholders,
} from "../utils/imagePlaceholders";
import zelij2 from "../assets/zelij2.jpg";

// Cloudinary Marrakech image
const CLOUDINARY_MARRAKECH_IMAGE =
  "https://res.cloudinary.com/dzszitsgb/image/upload/v1773760319/nouzha/ubllqf3hfvqox1qixeby.jpg";
const CLOUDINARY_FES_IMAGE =
  "https://res.cloudinary.com/dzszitsgb/image/upload/v1773759766/nouzha/fes-20260317-new.jpg";
const CLOUDINARY_SAHARA_IMAGE =
  "https://res.cloudinary.com/dzszitsgb/image/upload/v1773759769/nouzha/sahara-20260317-new.jpg";
const HOME_DESTINATION_IMAGES = {
  marrakech:
    "https://res.cloudinary.com/dzszitsgb/image/upload/v1773760319/nouzha/ubllqf3hfvqox1qixeby.jpg",
  fes:
    "https://res.cloudinary.com/dzszitsgb/image/upload/v1773759766/nouzha/fes-20260317-new.jpg",
  sahara:
    "https://res.cloudinary.com/dzszitsgb/image/upload/v1773759769/nouzha/sahara-20260317-new.jpg",
  chefchaouen:
    "https://res.cloudinary.com/dzszitsgb/image/upload/v1773766008/nouzha/destination-chefchaoun-20260317.jpg",
  essaouira:
    "https://res.cloudinary.com/dzszitsgb/image/upload/v1773768781/nouzha/destination-essaouira-20260317.jpg",
  casablanca:
    "https://res.cloudinary.com/dzszitsgb/image/upload/v1773766011/nouzha/destination-tangerrabat-20260317.jpg",
};

const DEFAULT_SIGNATURE_CIRCUITS = [
  {
    id: "atlas-luxe",
    title: "Atlas Prestige",
    subtitle: "Marrakech • Vallées berbères • Désert",
    duration: "7 jours",
    image: CLOUDINARY_MARRAKECH_IMAGE,
  },
  {
    id: "sahara-signature",
    title: "Sahara Signature",
    subtitle: "Ouarzazate • Merzouga • Dunes dorées",
    duration: "5 jours",
    image: CLOUDINARY_SAHARA_IMAGE,
  },
  {
    id: "imperial-route",
    title: "Route Impériale",
    subtitle: "Fès • Meknès • Rabat • Casablanca",
    duration: "6 jours",
    image: CLOUDINARY_FES_IMAGE,
  },
];

const DEFAULT_CURATED_COLLECTIONS = [
  {
    id: "weekend-fes",
    title: "Week-end depuis Fes",
    subtitle: "Escapades courtes, confort premium",
    duration: "2-3 jours",
    image: destinationPlaceholders.fez,
  },
  {
    id: "desert-luxe",
    title: "Desert Luxe",
    subtitle: "Camp haut de gamme et ciel etoile",
    duration: "3-5 jours",
    image: destinationPlaceholders.sahara,
  },
  {
    id: "family-morocco",
    title: "Family Friendly",
    subtitle: "Itineraires souples pour familles",
    duration: "4-7 jours",
    image: destinationPlaceholders.marrakech,
  },
];

/** Base URL backend (dev/prod) */
const PROD_API_BASE = "https://nouzhaback-theta.vercel.app";
const LOCAL_API_BASE = "http://localhost:5000";
const IS_LOCALHOST = typeof window !== "undefined" && ["localhost", "127.0.0.1"].includes(window.location.hostname);
const API_BASE_CANDIDATES = [
  IS_LOCALHOST ? LOCAL_API_BASE : null,
  process.env.REACT_APP_API_BASE_URL,
  process.env.VITE_API_BASE_URL,
  PROD_API_BASE,
]
  .filter(Boolean)
  .map((u) => u.replace(/\/$/, ""))
  .filter((u, i, arr) => arr.indexOf(u) === i);

const Home = ({ language, setLanguage }) => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  // const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // const [isScrolled, setIsScrolled] = useState(false);
  const t = translations[language];

  // -------------------------
  // Testimonials (dynamic)
  // -------------------------
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialsLoading, setTestimonialsLoading] = useState(true);
  const [testimonialsError, setTestimonialsError] = useState("");

  const signatureCircuits = useMemo(
    () => [
      {
        ...DEFAULT_SIGNATURE_CIRCUITS[0],
        title: t?.homeCircuit1Title || DEFAULT_SIGNATURE_CIRCUITS[0].title,
        subtitle: t?.homeCircuit1Subtitle || DEFAULT_SIGNATURE_CIRCUITS[0].subtitle,
        duration: t?.homeCircuit1Duration || DEFAULT_SIGNATURE_CIRCUITS[0].duration,
      },
      {
        ...DEFAULT_SIGNATURE_CIRCUITS[1],
        title: t?.homeCircuit2Title || DEFAULT_SIGNATURE_CIRCUITS[1].title,
        subtitle: t?.homeCircuit2Subtitle || DEFAULT_SIGNATURE_CIRCUITS[1].subtitle,
        duration: t?.homeCircuit2Duration || DEFAULT_SIGNATURE_CIRCUITS[1].duration,
      },
      {
        ...DEFAULT_SIGNATURE_CIRCUITS[2],
        title: t?.homeCircuit3Title || DEFAULT_SIGNATURE_CIRCUITS[2].title,
        subtitle: t?.homeCircuit3Subtitle || DEFAULT_SIGNATURE_CIRCUITS[2].subtitle,
        duration: t?.homeCircuit3Duration || DEFAULT_SIGNATURE_CIRCUITS[2].duration,
      },
    ],
    [t]
  );

  const curatedCollections = useMemo(
    () => [
      {
        ...DEFAULT_CURATED_COLLECTIONS[0],
        title: t?.homeCollection1Title || DEFAULT_CURATED_COLLECTIONS[0].title,
        subtitle: t?.homeCollection1Subtitle || DEFAULT_CURATED_COLLECTIONS[0].subtitle,
        duration: t?.homeCollection1Duration || DEFAULT_CURATED_COLLECTIONS[0].duration,
      },
      {
        ...DEFAULT_CURATED_COLLECTIONS[1],
        title: t?.homeCollection2Title || DEFAULT_CURATED_COLLECTIONS[1].title,
        subtitle: t?.homeCollection2Subtitle || DEFAULT_CURATED_COLLECTIONS[1].subtitle,
        duration: t?.homeCollection2Duration || DEFAULT_CURATED_COLLECTIONS[1].duration,
      },
      {
        ...DEFAULT_CURATED_COLLECTIONS[2],
        title: t?.homeCollection3Title || DEFAULT_CURATED_COLLECTIONS[2].title,
        subtitle: t?.homeCollection3Subtitle || DEFAULT_CURATED_COLLECTIONS[2].subtitle,
        duration: t?.homeCollection3Duration || DEFAULT_CURATED_COLLECTIONS[2].duration,
      },
    ],
    [t]
  );

  const fallbackTestimonials = useMemo(
    () => [
      {
        _id: "fallback-1",
        stars: 5,
        comment: t?.testimonial1 || "Séjour inoubliable à Marrakech ! Le riad était magnifique, l'équipe très accueillante et les circuits bien organisés. Je recommande vivement.",
        name: "Fatima Alami",
        city: "Casablanca",
        country: "Maroc",
        profilePhoto: { url: profilePlaceholders.female1 },
      },
      {
        _id: "fallback-2",
        stars: 5,
        comment: t?.testimonial2 || "Excellent service de location de voiture. Nous avons visité Fès, Chefchaouen et le désert sans aucun problème. Très professionnel !",
        name: "Ahmed Benali",
        city: "Rabat",
        country: "Maroc",
        profilePhoto: { url: profilePlaceholders.male1 },
      },
      {
        _id: "fallback-3",
        stars: 5,
        comment: t?.testimonial3 || "Visite guidée exceptionnelle de la médina de Fès. Notre guide était passionnant et très connaisseur. Une expérience authentique !",
        name: "Aicha Tazi",
        city: "Tanger",
        country: "Maroc",
        profilePhoto: { url: profilePlaceholders.female2 },
      },
    ],
    [t]
  );

  /*
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  */

  // Fetch 3 commentaires active
  useEffect(() => {
    let isMounted = true;

    const fetchActiveTestimonials = async () => {
      try {
        setTestimonialsLoading(true);
        setTestimonialsError("");

        let data = [];
        let lastErr = null;

        for (const base of API_BASE_CANDIDATES) {
          const url = `${base}/api/commentaires?status=active&limit=3`;
          try {
            const res = await fetch(url, {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
              },
            });

            if (!res.ok) {
              const text = await res.text().catch(() => "");
              throw new Error(
                `API error (${res.status}) ${text ? `- ${text}` : ""}`
              );
            }

            data = await res.json();
            lastErr = null;
            break;
          } catch (err) {
            lastErr = err;
          }
        }

        if (lastErr) {
          throw lastErr;
        }

        if (!isMounted) return;
        setTestimonials(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!isMounted) return;
        setTestimonialsError(
          `${t?.testimonialsLoadErrorPrefix || 'Unable to load testimonials'}: ${e.message}. ${t?.testimonialsLoadErrorFallback || 'Showing examples.'}`
        );
        setTestimonials([]);
      } finally {
        if (!isMounted) return;
        setTestimonialsLoading(false);
      }
    };

    fetchActiveTestimonials();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Message de bienvenue et bouton de déconnexion pour utilisateur connecté
  const WelcomeMessage = () => {
    if (!currentUser) return null;
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-gradient-to-r from-orange-100 to-red-100 py-4 px-6 rounded-xl shadow-sm flex justify-between items-center max-w-7xl mx-auto my-6"
      >
        <h2 className="text-xl font-semibold text-gray-800">
          {t.welcome},{" "}
          <span className="font-bold text-orange-600">
            {currentUser.username}
          </span>
          !
        </h2>
        <button
          onClick={logout}
          className="flex items-center gap-2 bg-white py-2 px-4 rounded-lg shadow-sm hover:bg-gray-50 text-gray-700 transition-all hover:shadow-md"
        >
          <LogOut size={18} />
          {t.logout}
        </button>
      </motion.div>
    );
  };

  // Animation variants
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const testimonialsToRender =
    testimonials && testimonials.length > 0 ? testimonials : fallbackTestimonials;

  return (
    <div
      className="w-full min-h-screen bg-white relative overflow-x-hidden font-inter"
      style={{
        backgroundImage: "url(/src/assets/zelij3.png)",
        backgroundRepeat: "repeat",
        backgroundSize: "400px",
        backgroundAttachment: "fixed",
        zIndex: 0,
      }}
    >
      <div className="absolute inset-0 bg-white/80 z-0 pointer-events-none" />
      <div className="relative z-10">
        <HeroSection language={language} setLanguage={setLanguage} t={t} />

        {/* Message de bienvenue pour utilisateur connecté */}
        <WelcomeMessage />

        {/* Signature circuits */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="py-10 px-6 my-10 mx-auto max-w-7xl"
        >
          <div className="rounded-3xl overflow-hidden border border-orange-200/70 shadow-2xl bg-gradient-to-br from-stone-900 via-orange-950 to-stone-800">
            <div className="px-6 md:px-10 pt-10 pb-8 text-white">
              <div className="text-xs uppercase tracking-[0.2em] text-orange-200/90 mb-3">
                {t?.homePremiumBadge || "Collection Premium"}
              </div>
              <h2 className="text-3xl md:text-5xl font-extrabold leading-tight">
                {t?.homePremiumTitle || "Circuits visuels, style agence haut de gamme"}
              </h2>
              <p className="mt-4 text-orange-100/90 max-w-3xl text-base md:text-lg">
                {t?.homePremiumDescription || "Vivez l'expérience marocaine ultime avec nos circuits soigneusement élaborés."}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-0.5 bg-orange-200/20">
              {signatureCircuits.map((circuit) => (
                <article
                  key={circuit.id}
                  className="group relative h-72 md:h-80 bg-stone-800 overflow-hidden cursor-pointer"
                  onClick={() => navigate("/circuit")}
                >
                  <img
                    src={circuit.image}
                    alt={circuit.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 opacity-90 group-hover:opacity-100"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-80 group-hover:opacity-60 transition-opacity" />
                  
                  <div className="absolute bottom-0 p-6 text-white w-full">
                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider mb-2 text-orange-300">
                      <Calendar className="w-4 h-4" />
                      {circuit.duration}
                    </div>
                    <h3 className="text-2xl font-bold mb-1">{circuit.title}</h3>
                    <p className="text-sm text-orange-50/80 flex items-center gap-2">
                       <MapPin className="w-4 h-4" />
                       <span className="truncate">{circuit.subtitle}</span>
                    </p>
                  </div>
                </article>
              ))}
            </div>

            <div className="px-6 md:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-4 border-t border-white/10">
              <p className="text-orange-100/80 text-sm">
                {t?.homePremiumTrustLine || "Des centaines de voyageurs nous ont déjà fait confiance."}
              </p>
              <button
                onClick={() => navigate("/circuit")}
                className="flex items-center gap-2 rounded-full bg-white text-orange-900 px-6 py-2.5 font-bold hover:bg-orange-50 transition-colors"
              >
                {t?.homePremiumSeeAll || "Tout voir"}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </motion.section>

        {/* Curated collections */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="py-10 px-6 my-10 mx-auto max-w-7xl"
        >
          <div className="flex items-end justify-between gap-4 mb-6">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-orange-600">{t?.homeCollectionsBadge || "Collections"}</div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900">{t?.homeCollectionsTitle || "Inspiration du moment"}</h2>
            </div>
            <button
              onClick={() => navigate("/circuit")}
              className="hidden md:inline-flex items-center gap-2 text-orange-700 font-semibold"
            >
              {t?.homeCollectionsSeeAll || "Voir tout"}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {curatedCollections.map((item) => (
              <article
                key={item.id}
                onClick={() => navigate("/circuit")}
                className="group rounded-2xl overflow-hidden border border-orange-100 bg-white shadow-sm hover:shadow-xl transition-shadow cursor-pointer"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/65 to-transparent" />
                  <div className="absolute bottom-3 left-3 px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm text-white text-xs font-semibold">
                    {item.duration}
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="text-xl font-bold text-gray-900">{item.title}</h3>
                  <p className="mt-2 text-gray-600">{item.subtitle}</p>
                </div>
              </article>
            ))}
          </div>
        </motion.section>

        {/* Floating decorative elements */}
        <div className="fixed -right-20 -bottom-20 w-96 h-96 rounded-full bg-gradient-to-r from-orange-100 to-red-100 opacity-20 blur-3xl -z-10"></div>
        <div className="fixed -left-20 top-1/3 w-64 h-64 rounded-full bg-gradient-to-r from-orange-100 to-red-100 opacity-20 blur-3xl -z-10"></div>

        {/* Services Section */}
        <motion.section
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="py-20 px-6 bg-gradient-to-br from-orange-50/90 to-red-50/90 rounded-3xl shadow-lg my-12 mx-auto max-w-7xl backdrop-blur-sm"
        >
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
                {t.servicesTitle}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {t.servicesSubtitle}
              </p>
            </motion.div>

            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              <motion.div variants={item}>
                <ServiceCard
                  image={servicePlaceholders.riads}
                  title={t.riads}
                  description={t.riadsDesc}
                  cta={t.exploreOptions}
                  to="/sejour"
                />
              </motion.div>

              <motion.div variants={item}>
                <ServiceCard
                  image={servicePlaceholders.circuits}
                  title={t.circuits}
                  description={t.circuitsDesc}
                  cta={t.viewCircuits}
                  to="/circuit"
                />
              </motion.div>

              <motion.div variants={item}>
                <ServiceCard
                  image={servicePlaceholders.carRental}
                  title={t.carRental}
                  description={t.carRentalDesc}
                  cta={t.rentNow}
                  to="/location"
                />
              </motion.div>

              <motion.div variants={item}>
                <ServiceCard
                  image={servicePlaceholders.transport}
                  title={t.transport}
                  description={t.transportDesc}
                  cta={t.bookTransport}
                  to="/transport"
                />
              </motion.div>

              <motion.div variants={item}>
                <ServiceCard
                  image={servicePlaceholders.guidedTours}
                  title={t.guidedTours}
                  description={t.guidedToursDesc}
                  cta={t.browseTours}
                  to="/guided-tours"
                />
              </motion.div>

              <motion.div variants={item}>
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden group hover:shadow-2xl transition-all duration-300 border border-orange-200 hover:border-orange-300">
                  <div className="relative h-48 overflow-hidden bg-gradient-to-br from-orange-200 to-red-200 flex items-center justify-center">
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 90 }}
                      className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center"
                    >
                      <span className="text-white text-2xl font-bold">+</span>
                    </motion.div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 text-white">
                      <h3 className="text-xl font-bold">{t.seeAll}</h3>
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="text-gray-600 mb-4">{t.discoverAllServices}</p>
                    <button
                      onClick={() => navigate("/services")}
                      className="flex items-center text-orange-600 font-semibold group-hover:text-orange-700"
                    >
                      {t.seeAll}
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </motion.section>

        {/* Why Choose Us Section */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="py-24 px-6 my-16 mx-auto max-w-7xl"
        >
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="text-center mb-16"
            >
              <motion.span
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                className="inline-block px-4 py-1.5 text-xs font-bold text-orange-600 bg-orange-100 rounded-full uppercase tracking-wider mb-6 shadow-sm"
              >
                {t.whyChooseUs || "Why Choose Us"}
              </motion.span>
              <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6 tracking-tight">
                {t.whyChooseTitle || "Pourquoi Choisir Palais Touristique?"}
              </h2>
              <div className="w-24 h-1.5 bg-gradient-to-r from-orange-400 to-amber-600 mx-auto mb-8 rounded-full" />
              <p className="text-xl text-gray-600 max-w-2xl mx-auto font-light leading-relaxed">
                {t.whyChooseSubtitle || "Votre partenaire de confiance pour des expériences marocaines authentiques"}
              </p>
            </motion.div>

            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 items-stretch"
            >
              <motion.div variants={item} className="h-[400px]">
                <FeatureCard
                  icon={<Award />}
                  title={t.localExpertise || "Expertise Locale"}
                  description={t.localExpertiseDesc || "Guides natifs avec une connaissance approfondie de la culture marocaine"}
                  image="https://res.cloudinary.com/dzszitsgb/image/upload/v1773759766/nouzha/fes-20260317-new.jpg"
                />
              </motion.div>

              <motion.div variants={item} className="h-[400px]">
                <FeatureCard
                  icon={<DollarSign />}
                  title={t.bestPrices || "Meilleurs Prix"}
                  description={t.bestPricesDesc || "Tarifs compétitifs sans frais cachés et garantie de prix"}
                  image="https://res.cloudinary.com/dzszitsgb/image/upload/v1773760319/nouzha/ubllqf3hfvqox1qixeby.jpg"
                />
              </motion.div>

              <motion.div variants={item} className="h-[400px]">
                <FeatureCard
                  icon={<Headphones />}
                  title={t.support || "Support"}
                  description={t.supportDesc || "Assistance 24h/24 dans plusieurs langues"}
                  image="https://res.cloudinary.com/dzszitsgb/image/upload/v1773759769/nouzha/sahara-20260317-new.jpg"
                />
              </motion.div>

              <motion.div variants={item} className="h-[400px]">
                <FeatureCard
                  icon={<Shield />}
                  title={t.authenticity || "Expériences Authentiques"}
                  description={t.authenticityDesc || "Immersion culturelle authentique avec les familles locales"}
                  image="https://res.cloudinary.com/dzszitsgb/image/upload/v1773766008/nouzha/destination-chefchaoun-20260317.jpg"
                />
              </motion.div>
            </motion.div>
          </div>
        </motion.section>

        {/* Destinations Section */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="py-20 px-6 bg-gradient-to-br from-red-50/90 to-orange-50/90 rounded-3xl shadow-lg my-12 mx-auto max-w-7xl backdrop-blur-sm"
        >
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
                {t.destinationsTitle || "Destinations Populaires"}
              </h2>
              <p className="text-xl text-gray-600">{t.destinationsSubtitle || "Découvrez les lieux les plus enchanteurs du Maroc"}</p>
            </motion.div>

            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              <motion.div variants={item}>
                <DestinationCard
                  image={HOME_DESTINATION_IMAGES.marrakech}
                  title={t.marrakech || "Marrakech"}
                  description={t.marrakechDesc || "La Ville Rouge avec ses souks animés et palais historiques"}
                  price="80"
                  badge={t.popular || "Populaire"}
                  badgeColor="orange"
                  slug="Marrakech"
                  language={language}
                />
              </motion.div>

              <motion.div variants={item}>
                <DestinationCard
                  image={HOME_DESTINATION_IMAGES.fes}
                  title={t.fez || "Fès"}
                  description={t.fezDesc || "La capitale culturelle avec la plus grande médina du monde"}
                  price="65"
                  badge={t.cultural || "Culturel"}
                  badgeColor="blue"
                  slug="Fès"
                  language={language}
                />
              </motion.div>

              <motion.div variants={item}>
                <DestinationCard
                  image={HOME_DESTINATION_IMAGES.casablanca}
                  title={t.casablanca || "Casablanca"}
                  description={t.casablancaDesc || "Métropole moderne avec la magnifique Mosquée Hassan II"}
                  price="70"
                  badge={t.modern || "Moderne"}
                  badgeColor="green"
                  slug="Casablanca"
                  language={language}
                />
              </motion.div>

              <motion.div variants={item}>
                <DestinationCard
                  image={HOME_DESTINATION_IMAGES.sahara}
                  title={t.sahara || "Désert du Sahara"}
                  description={t.saharaDesc || "Camps dans le désert et randonnées à dos de chameau inoubliables"}
                  price="120"
                  badge={t.adventure || "Aventure"}
                  badgeColor="yellow"
                  slug="Merzouga"
                  language={language}
                />
              </motion.div>

              <motion.div variants={item}>
                <DestinationCard
                  image={HOME_DESTINATION_IMAGES.chefchaouen}
                  title={t.chefchaouen || "Chefchaouen"}
                  description={t.chefchaouenDesc || "La perle bleue nichée dans les montagnes du Rif"}
                  price="55"
                  badge={t.unique || "Unique"}
                  badgeColor="indigo"
                  slug="Chefchaouen"
                  language={language}
                />
              </motion.div>

              <motion.div variants={item}>
                <DestinationCard
                  image={HOME_DESTINATION_IMAGES.essaouira}
                  title={t.essaouira || "Essaouira"}
                  description={t.essaouiraDesc || "Charme côtier avec architecture portugaise et fruits de mer frais"}
                  price="60"
                  badge={t.coastal || "Côtier"}
                  badgeColor="cyan"
                  slug="Essaouira"
                  language={language}
                />
              </motion.div>
            </motion.div>
          </div>
        </motion.section>

        {/* Testimonials Section (Dynamic) */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="py-20 px-6 bg-white/90 rounded-3xl shadow-lg my-12 mx-auto max-w-7xl backdrop-blur-sm"
        >
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
                {t.testimonialsTitle}
              </h2>
              <p className="text-xl text-gray-600">{t.testimonialsSubtitle}</p>
            </motion.div>

            {/* Loading / Error */}
            {testimonialsLoading && (
              <div className="flex items-center justify-center gap-3 text-gray-600">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Chargement des témoignages...</span>
              </div>
            )}

            {!testimonialsLoading && testimonialsError && (
              <div className="flex items-center justify-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8">
                <AlertTriangle className="w-5 h-5" />
                <span className="text-sm">{testimonialsError}</span>
              </div>
            )}

            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {testimonialsToRender.slice(0, 3).map((c, idx) => (
                <motion.div variants={item} key={c?._id || idx}>
                  <TestimonialCard
                    stars={Number(c?.stars || 5)}
                    review={c?.comment || ""}
                    name={c?.name || "Client"}
                    location={`${c?.city || ""}${c?.country ? `, ${c.country}` : ""}`}
                    image={c?.profilePhoto?.url || profilePlaceholders.female1}
                  />
                </motion.div>
              ))}
            </motion.div>
          </div>
        </motion.section>

        {/* CTA Section */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="py-20 px-6 text-white rounded-3xl shadow-xl my-12 mx-auto max-w-7xl relative overflow-hidden"
          style={{
            backgroundImage: `url(${zelij2})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-orange-500/70 to-red-600/70 z-0" />
          <div className="relative z-10 max-w-6xl mx-auto text-center">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-4xl md:text-5xl font-bold mb-6"
            >
              {t.ctaTitle}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-xl mb-12 opacity-90 max-w-3xl mx-auto"
            >
              {t.ctaSubtitle}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-6 justify-center"
            >
              <a href="/services">
                <button className="bg-white text-orange-600 px-10 py-4 rounded-xl font-bold hover:bg-gray-100 transition-all transform hover:scale-105 shadow-lg hover:shadow-xl">
                  {t.startBooking}
                </button>
              </a>
              <a href="/contact">
                <button className="bg-transparent border-2 border-white text-white px-10 py-4 rounded-xl font-bold hover:bg-white/10 transition-all transform hover:scale-105">
                  {t.contactUs}
                </button>
              </a>
            </motion.div>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

// Enhanced Reusable Components with Animations
const FeatureCard = ({ icon, title, description, image }) => {
  return (
    <motion.div
      whileHover={{ y: -10 }}
      className="relative h-full overflow-hidden rounded-3xl group cursor-default shadow-xl"
    >
      <div
        className="absolute inset-0 bg-cover bg-center transition-transform duration-1000 group-hover:scale-110"
        style={{ backgroundImage: `url(${image})` }}
      />

      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/10 opacity-70 group-hover:opacity-85 transition-opacity duration-500" />

      <div className="absolute inset-0 border border-white/10 rounded-3xl group-hover:border-orange-500/30 transition-colors duration-500" />

      <div className="relative h-full p-8 flex flex-col items-center justify-end text-center z-10">
        <motion.div
          whileHover={{ rotate: 360, scale: 1.1 }}
          transition={{ type: "spring", stiffness: 200, damping: 10 }}
          className="w-16 h-16 bg-orange-500/30 backdrop-blur-xl rounded-2xl flex items-center justify-center mb-6 border border-white/20 group-hover:border-orange-400 group-hover:bg-orange-500/50 transition-all duration-500 shadow-2xl"
        >
          {React.cloneElement(icon, {
            className: "w-8 h-8 text-white group-hover:text-amber-200 transition-colors",
            size: 32,
          })}
        </motion.div>

        <h3 className="text-2xl font-black text-white mb-4 tracking-tight drop-shadow-md group-hover:text-orange-300 transition-colors duration-300">
          {title}
        </h3>

        <div className="overflow-hidden">
          <p className="text-gray-200 text-sm leading-relaxed font-medium opacity-90 lg:opacity-0 lg:group-hover:opacity-100 transform lg:translate-y-8 lg:group-hover:translate-y-0 transition-all duration-700 ease-out drop-shadow-sm">
            {description}
          </p>
        </div>

        <div className="w-16 h-1 bg-gradient-to-r from-orange-400 to-amber-600 mt-6 rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-center shadow-[0_0_15px_rgba(251,191,36,0.5)]" />
      </div>

      <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-orange-500/20 blur-[60px] rounded-full group-hover:bg-orange-500/40 transition-colors duration-1000" />
    </motion.div>
  );
};

const DestinationCard = ({ image, title, description, price, badge, badgeColor, slug, language }) => {
  const navigate = useNavigate();
  const t = translations[language];

  const badgeClasses = {
    orange: "bg-orange-500",
    blue: "bg-blue-500",
    green: "bg-green-500",
    yellow: "bg-yellow-500",
    indigo: "bg-indigo-500",
    cyan: "bg-cyan-500",
  };

  return (
    <motion.div
      whileHover={{ y: -12 }}
      onClick={() => navigate(`/circuit?city=${encodeURIComponent(slug)}`)}
      className="relative bg-white rounded-3xl shadow-xl overflow-hidden group cursor-pointer transition-all duration-500 hover:shadow-[0_20px_50px_rgba(251,146,60,0.2)] border border-orange-100/50"
    >
      <div className="relative h-64 overflow-hidden">
        <motion.img
          src={image}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
          onError={(e) => {
            e.currentTarget.src =
              "https://res.cloudinary.com/dzszitsgb/image/upload/v1773760319/nouzha/ubllqf3hfvqox1qixeby.jpg";
          }}
        />

        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-500" />

        {badge && (
          <motion.div
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            className={`absolute top-5 left-5 text-white px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider shadow-lg ${badgeClasses[badgeColor]}`}
          >
            {badge}
          </motion.div>
        )}

        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
          <motion.div
            initial={{ scale: 0.8, y: 20 }}
            whileHover={{ scale: 1.1 }}
            className="bg-white/90 backdrop-blur-md text-orange-600 font-bold px-6 py-3 rounded-2xl shadow-2xl flex items-center gap-2"
          >
            {language === "fr" ? "Découvrir" : "Discover"}
            <ArrowRight size={18} />
          </motion.div>
        </div>
      </div>

      <div className="p-8 relative">
        <div className="flex justify-between items-start mb-3">
          <h3 className="text-2xl font-bold text-gray-900 group-hover:text-orange-600 transition-colors duration-300">
            {title}
          </h3>
        </div>

        <p className="text-gray-600 mb-8 line-clamp-2 text-sm leading-relaxed">{description}</p>

        <div className="flex items-center justify-between pt-6 border-t border-orange-50">
          <div>
            <p className="text-sm font-bold text-orange-400 uppercase tracking-widest mb-1">
              {t.from || (language === "fr" ? "À partir de" : "From")}
            </p>
            <span className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">
              {price} €
            </span>
          </div>

          <motion.div
            whileHover={{ x: 5 }}
            className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center text-orange-600 group-hover:bg-orange-600 group-hover:text-white transition-all duration-300"
          >
            <ArrowRight size={20} />
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 h-1.5 w-0 bg-gradient-to-r from-orange-400 to-red-600 group-hover:w-full transition-all duration-700 ease-in-out" />
      </div>
    </motion.div>
  );
};

const TestimonialCard = ({ stars, review, name, location, image }) => (
  <motion.div
    whileHover={{ scale: 1.02 }}
    className="bg-gray-50 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 border border-gray-200"
  >
    <div className="flex items-center mb-4">
      <div className="flex text-yellow-500 mb-2">
        {[...Array(Math.max(1, Math.min(5, Number(stars) || 5)))].map((_, i) => (
          <Star key={i} className="w-5 h-5 fill-current" />
        ))}
      </div>
    </div>
    <p className="text-gray-700 mb-6 italic">"{review}"</p>
    <div className="flex items-center">
      <motion.img
        src={image}
        alt={name}
        className="w-12 h-12 rounded-full mr-4 object-cover"
        whileHover={{ rotate: 5 }}
      />
      <div>
        <h4 className="font-semibold text-gray-800">{name}</h4>
        <p className="text-sm text-gray-500">{location}</p>
      </div>
    </div>
  </motion.div>
);

export default Home;
